<script lang="ts">
  import { addDays, debouncer, findModifiedDates, getForecastResultsForChart } from '@/Util/utils';
  import TodayActivitiesSkeleton from '@/MyFitness/Today/TodayActivitiesSkeleton.svelte';
  import { getContext, onMount, setContext, tick } from 'svelte';
  import type { MedianAndWeatherStores, MyFitnessStores } from '@/MyFitness/stores';
  import UpcomingTraining from '@/MyFitness/Today/UpcomingTraining.svelte';
  import { getXSSTargets, populateForecast, type ForecastResult, type XSSTargetsResponse } from '@/Util/api';
  import { Activity, type Exercise } from '@/Common/ActivityFunctions';
  import CalendarAddModal from '@/MyFitness/Calendar/CalendarAddModal.svelte';
  import { FORECAST_TOOLS, type ForecastTools } from './forecastTools';
  import HorizontalScrollingTrainingCards from './HorizontalScrollingTrainingCards.svelte';
  import TodayActivities from './TodayActivities.svelte';
  import WorkoutPlayers from './WorkoutPlayers.svelte';
  import XataColumn from './XataColumn.svelte';
  import PrePlanCard from './PrePlanCard.svelte';
  import WorkoutGenerator from './WorkoutGenerator.svelte';
  import Skeleton from '@/lib/components/ui/skeleton/skeleton.svelte';
  import HorizontalScrollingTrainingCardsSkeleton from './HorizontalScrollingTrainingCardsSkeleton.svelte';
  import XssSummary from './XssSummary.svelte';
  import RecoverySummary from './RecoverySummary.svelte';
  import { activitiesForDate } from '@/Singletons/ActivitiesForDateWritable';
  import { DataStatus } from '@/Singletons/common';
  import { recommendedTraining } from '@/Singletons/RecommendedTrainingWritable';
  import { connectedWorkoutPlayers } from '@/Singletons/WorkoutPlayersWritable';
  import UserInfo from './UserInfo.svelte';
  import StickyScrollColumn from './StickyScrollColumn.svelte';
  import { SearchFilterFieldsAndData } from './SearchFilterFieldsAndData';
  import * as Dialog from '$lib/components/ui/dialog';
  import SearchFilter from './SearchFilter.svelte';
  import { Button } from '@/lib/components/ui/button';
  import { fromUnixTime, isSameDay, startOfDay } from 'date-fns';
  import XataSummary from './XataSummary.svelte';
  import XSSDisplay from '@/Common/XSSDisplay.svelte';
  import { MagicBucketsFieldsAndData } from '../MagicBuckets/MagicBucketsFieldsAndData';
  import TodayMagicBuckets from './TodayMagicBuckets.svelte';

  export let athlete: string;
  export let workoutDescription: string;
  export let lastBreakthrough: {
    name: string;
    path: string;
    start_date: string;
    stale: boolean;
  };

  export let sport_type: string;
  export let activity_types;
  export let likeScore: number;
  export let dislikeScore: number;
  export let program_weeks;
  export let challenge_days;

  const myFitness = getContext<MyFitnessStores>('myFitness');
  const { trainingMedians, weather, locationPermission } = getContext<MedianAndWeatherStores>('medianAndWeather');
  const { trainingPlan, cal_settings, trainingAdvice } = myFitness;

  // Batch-load comment counts for the currently-shown day's activities, for the CommentsButton
  // on each TodayActivityCard -- one request per day-load instead of one per card.
  $: if ($activitiesForDate.status === DataStatus.READY) {
    myFitness.loadCommentCounts(
      $activitiesForDate.activities.filter((a) => Activity.isActivity(a)).map((a) => a.path).filter((p): p is string => !!p)
    );
  }

  //@ts-ignore
  const Xert = window.Xert;

  let currentDay = new Date();
  let rootDiv: HTMLElement;
  let CalendarAddModalElement;

  function openCalendarAddModalEdit(event) {
    CalendarAddModalElement.initEdit(event.detail.date, event.detail.exercise, event.detail.setIsLoading, event?.detail?.openTo);
  }

  // *****************************
  // This section is for the upcoming forecasted training
  // *****************************

  let forecastResult: ForecastResult | null = null;
  let forecastRequest = 0; // Declared before the first getForecastResult() call below, which uses it
  let forecastPromise = getForecastResult();
  let isResyncingForecast = false;

  // Next 3 Days always starts tomorrow, whichever day the page is showing.
  async function getForecastResult() {
    const request = ++forecastRequest;
    const result = $trainingPlan ? await populateForecast($trainingPlan.selected_mode, addDays(new Date(), 1)) : null;
    if (request === forecastRequest) forecastResult = result; // Drop a reply overtaken by a later request
  }

  const forecastDebouncer = debouncer(500);
  function resyncForecast() {
    isResyncingForecast = true;
    forecastDebouncer.debounce(async () => {
      try {
        await getForecastResult();
      } finally {
        isResyncingForecast = false;
      }
    });
  }

  // *****************************
  // This section is for the forecast chart
  // *****************************

  let hasConflictDays = false
  let todayConflictNotes: string[] = []
  // The settings are an argument only so that a change to the availability checks again.
  // Runs overlap when the settings change quickly: only the latest one's result is kept
  let forecastCheck = 0
  const updateForecastResults = async (trainingPlan: any, _calSettings: unknown = null) => {
    const run = ++forecastCheck
    const tpForecastResults = trainingPlan?.forecastResults ?? null
    if (tpForecastResults) {
      const result = await getForecastResultsForChart(tpForecastResults, trainingPlan.settings ?? null, $cal_settings, $trainingMedians, true)
      if (run !== forecastCheck) return
      hasConflictDays = !result.isForecastUpToDate
      todayConflictNotes = result.forecastResults.days.find(day => isSameDay(fromUnixTime(day.t), currentDay))?.notes?.split(', ') ?? [];
      console.log(todayConflictNotes);
    } else {
      hasConflictDays = false
    }
  }
  $: updateForecastResults($trainingPlan, $cal_settings)

  // The cards of the forecasted days: a change to a day's training (clear, pin) or availability checks again whether Adapt Forecast is needed.
  // Saving the availability also lights it at once through $cal_settings.
  setContext<ForecastTools>(FORECAST_TOOLS, {
    refreshAdaptIndicator: () => updateForecastResults($trainingPlan, $cal_settings),
  });

  let isForecastUpToDate = true
  $: lastForecastInputs = $trainingPlan?.forecastResults?.inputs?.days ?? null
  $: if ($trainingPlan?.isForecastPlan && $cal_settings?.defaults && $cal_settings?.availabilities && lastForecastInputs) {
    // We are in target_focus or target_xss, need to make sure the last forecast's inputs have not been changed
    // If they have, we need to adapt the forecast
    const shouldAdaptAvail = findModifiedDates(lastForecastInputs, $cal_settings.defaults, $cal_settings.availabilities)
    isForecastUpToDate = !shouldAdaptAvail && !hasConflictDays
  } else {
    isForecastUpToDate = true
  }

  // // TODO (hyde): maybe show chart on a third column right of main content
  // let chartData = [];

  // // React to TrainingAdvice and trainingPlan
  // const trainingPlanDebouncer = debouncer(250);
  // $: if ($trainingPlan) {
  //   // rerun query everytime training advice changes.

  //   // Use debouncer since when saving new training plan, multiple updates are done during the process. For example old placeholder activities are first deleted, then new ones are scheduled.
  //   trainingPlanDebouncer.debounce(() => updateTrainingPlan($trainingPlan));
  // }

  // async function updateTrainingPlan(trainingPlan) {
  //   isResyncingForecast = true;
  //   await getForecastResult();
  //   isResyncingForecast = false;

  //   if (trainingPlan && trainingPlan.program_type == 'targetEvent' && trainingPlan.forecastResults) {
  //     const results = trainingPlan.forecastResults;
  //     const days = results.days;
  //     const n = days.length;

  //     if (n > 0) {
  //       const result = await getForecastResultsForChart(results, trainingPlan.settings, $cal_settings, $trainingMedians);
  //       chartData = result.forecastResults['days'];
  //     }
  //   }
  // }

  // *****************************
  // This section is for recommended training and the choosable training cards
  // *****************************

  $: recommendedTrainingExerciseGroups = createRecommendedTrainingGroups($recommendedTraining.exercises);

  function createRecommendedTrainingGroups(exercises: Exercise[]): Record<'Recommended' | 'Favourites' | 'Recent', { title: string, exercises: Exercise[] }> {
    if (!exercises || exercises.length === 0) {
      return {
        'Recommended': { title: 'Recommended', exercises: [] },
        'Favourites': { title: 'Favourites', exercises: [] },
        'Recent': { title: 'Recent', exercises: [] },
      };
    }
    
    const exerciseMap = {
      'Recommended': { title: 'Recommended', exercises: exercises },
      'Favourites': { title: 'Favourites', exercises: exercises.filter(ex => ex.favorite || ex.liked) },
      'Recent': {
        title: `Recent ${currentDay.toLocaleDateString('en-US', { weekday: 'long' })} Training`,
        exercises: exercises.filter(ex => ex.start_date && new Date(ex.start_date) < startOfDay(currentDay) && (new Date(ex.start_date).getDay() == currentDay.getDay()))
      },
    }

    // If no recent training for the current day of the week, fall back to weekend/weekday recent training,
    // then fallback to just recent training
    if (exerciseMap['Recent'].exercises.length == 0) {
      const isWeekend = (currentDay.getDay() == 0 || currentDay.getDay() == 6);
      exerciseMap['Recent'].title = isWeekend ? 'Recent Weekend Training' : 'Recent Weekday Training';
      exerciseMap['Recent'].exercises = exercises.filter(ex => {
        if (!ex.start_date) return false;
        const exDate = new Date(ex.start_date);
        const exIsWeekend = (exDate.getDay() == 0 || exDate.getDay() == 6);
        return exIsWeekend == isWeekend;
      });
      if (exerciseMap['Recent'].exercises.length == 0) {
        exerciseMap['Recent'].title = 'Recent Training';
        exerciseMap['Recent'].exercises = exercises.sort((a, b) => {
          const aDate = a.start_date ? new Date(a.start_date).getTime() : 0;
          const bDate = b.start_date ? new Date(b.start_date).getTime() : 0;
          return bDate - aDate;
        }).slice(0, 10);
      }
    }

    return exerciseMap;
  }

  let filterDialogOpen: boolean = false;
  let recommendedTrainingSearchFilters: SearchFilterFieldsAndData;
  let filteredRecommendedTrainingExercises: Exercise[] = [];
  
  // For search to work in the filters, the search filter has to be rebuilt anytime the exercises change
  $: if (recommendedTrainingExerciseGroups['Recommended'].exercises) {
    initFilters();
  }

  function initFilters() {
    recommendedTrainingSearchFilters = new SearchFilterFieldsAndData(
      recommendedTrainingExerciseGroups['Recommended'].exercises,
      (newFilteredExercises: Exercise[]) => {
        filteredRecommendedTrainingExercises = newFilteredExercises;
      },
      () => { recommendedTrainingSearchFilters = recommendedTrainingSearchFilters; }
    );
  }

  // *****************************
  // This section is for magic buckets
  // *****************************

  // For bind only
  export let xssTargetsResponse: XSSTargetsResponse = null;
  let xssTargetsLoading = true;
  loadDailySummary();

  const dailySummaryDebouncer = debouncer(200);
  const executeDailySummaryDebouncer = () => dailySummaryDebouncer.debounce(() => loadDailySummary());

  async function loadDailySummary() {
    xssTargetsResponse = await getXSSTargets(currentDay);
    dailySummaryResyncing = false;
    xssTargetsLoading = false;
  }

  let magicBucketsFieldsAndData: MagicBucketsFieldsAndData = new MagicBucketsFieldsAndData(async () =>{
    await tick();
    magicBucketsFieldsAndData = magicBucketsFieldsAndData; // trigger reactivity
  });
  $: magicBucketsFieldsAndData.signature = $trainingAdvice.signature;
  $: magicBucketsFieldsAndData.recommendedWorkoutDuration = ($trainingAdvice.availability ?? 0) * 3600;
  $: if (xssTargetsResponse?.remainingTargetsNumeric) updateTargets();
  function updateTargets() {
    magicBucketsFieldsAndData.updateTargets(xssTargetsResponse?.remainingTargetsNumeric);
  }

  // *****************************
  // End of section
  // *****************************

  function onDateChange(newDate: Date) {
    currentDay = newDate;
    activitiesForDate.changeDate(currentDay);
    recommendedTraining.changeDate(currentDay);
    loadDailySummary();
  }

  let dailySummaryResyncing: boolean = false;

  function reactiveReload(data) {
    if (data.refresh_date && data.refresh_date != '') {
      const endOfDay = new Date(currentDay);
      endOfDay.setHours(23, 59, 59, 999);
      if (new Date(data.refresh_date) <= endOfDay) {
        // Silent reload of activities, don't set the promise since we don't want to show a loading state
        dailySummaryResyncing = true;
        executeDailySummaryDebouncer();
      }
      // A change on or before the last of the next 3 days changes its activities or the signature they are explained with.
      const endOfNext3Days = addDays(new Date(), 3);
      endOfNext3Days.setHours(23, 59, 59, 999);
      if ($trainingPlan?.isForecastPlan && new Date(data.refresh_date) <= endOfNext3Days) {
        resyncForecast();
      }
    }
  }

  onMount(function () {
    activitiesForDate.init(currentDay);
    recommendedTraining.init(currentDay);
    connectedWorkoutPlayers.init();

    Xert._userChannel.bind('schedule_activity_complete', reactiveReload);
    Xert._userChannel.bind('modify_activity_complete', reactiveReload);
    Xert._userChannel.bind('delete_activity_complete', reactiveReload);
    Xert._userChannel.bind('upload_activity_complete', reactiveReload);
    Xert._userChannel.bind('trickle_complete', reactiveReload);
    Xert._userChannel.bind('sessions', reactiveReload);
    Xert._userChannel.bind('group_activity_update', reactiveReload);
  });

</script>

<div class="w-full flex flex-col items-center no-twp pt-[10px]" bind:this={rootDiv} on:editEvent={openCalendarAddModalEdit}>
  <div class="w-full 2xl:w-[80%] flex flex-row-reverse flex-wrap gap-8 no-twp max-w-[1300px] justify-center px-[4px]">
    <div class="min-w-[300px] flex h-max flex-1 flex-col gap-12">
      <!--CurrentDayTitleAndDateSelector {currentDay} {onDateChange} /-->
      {#if $activitiesForDate.status !== DataStatus.READY && $activitiesForDate.status !== DataStatus.RESYNCING}
        <TodayActivitiesSkeleton {currentDay} {onDateChange} />
      {:else}
        <TodayActivities
          todaysActivities={{
            activities: $activitiesForDate.activities,
            update: (updater) => activitiesForDate.update((prev) => {
              return {
                ...prev,
                activities: updater(prev.activities)
              };
            })
          }}
          {currentDay} isResyncing={$activitiesForDate.status === DataStatus.RESYNCING}
          conflictNotes={todayConflictNotes}
          {onDateChange}
          showAdapt={!!$trainingPlan?.isForecastPlan} {isForecastUpToDate} {lastForecastInputs}
        />
      {/if}

      {#if isSameDay(currentDay, new Date()) || currentDay < new Date()}
        <div class="flex flex-col">
          <div class='flex flex-row items-center gap-2 mb-2'>
            <h1 class="text-2xl font-semibold m-0">Daily Summary</h1>
            {#if dailySummaryResyncing}
              <i class='fa-solid fa-lg fa-spinner-third fa-spin' title="Resyncing..."></i>
            {/if}
          </div>

          <div class='flex flex-row flex-wrap gap-3'>
            <div class="flex flex-col flex-1 min-w-[340px] max-w-[400px] h-max gap-1 rounded-lg border border-border bg-white p-4 shadow-sm hover:shadow">
              <XssSummary bind:xssTargetsResponse bind:isLoading={xssTargetsLoading} />
            </div>

            <div class="flex flex-col flex-1 min-w-[300px] max-w-[400px] h-max gap-1 rounded-lg border border-border bg-white p-4 shadow-sm hover:shadow">
              <RecoverySummary bind:xssTargetsResponse isViewingToday={isSameDay(currentDay, new Date())} />
            </div>

            {#if $trainingPlan?.is_pre_plan}
              <div class="flex flex-col flex-1 lg:hidden min-w-[300px] max-w-[500px] h-max">
                <PrePlanCard {myFitness} />
              </div>
            {/if}
            {#if !$trainingPlan?.isForecastPlan}
              <div class="flex flex-col flex-1 lg:hidden min-w-[300px] max-w-[500px] h-max gap-1 rounded-lg border border-border bg-white p-4 shadow-sm hover:shadow">
                <XataSummary {myFitness} />
              </div>
            {/if}
          </div>
        </div>
      {/if}

      {#if isSameDay(currentDay, new Date()) || currentDay > new Date()}
        <div class="flex flex-col">
          <div class='flex flex-row items-center gap-2 mb-2'>
            <h1 class="text-2xl font-semibold m-0">Magic Buckets</h1>
            {#if $recommendedTraining.status === DataStatus.RESYNCING || dailySummaryResyncing}
              <i class='fa-solid fa-lg fa-spinner-third fa-spin' title="Resyncing..."></i>
            {/if}
          </div>

          <div class="flex flex-col gap-1 rounded-lg border border-border bg-white p-4 shadow-sm hover:shadow">
            <TodayMagicBuckets bind:fields={magicBucketsFieldsAndData} targetsSource={xssTargetsResponse?.source} />
          </div>
        </div>
      {/if}

      {#if isSameDay(currentDay, new Date())}
        <div class="flex flex-col">
          <h1 class="text-2xl font-semibold m-0">Workout Players</h1>
          <WorkoutPlayers />
        </div>
      {/if}

      {#if isSameDay(currentDay, new Date()) || currentDay > new Date()}
        <div class="flex flex-col">
          <div class='flex flex-row items-center gap-2 mb-2'>
            <h1 class="text-2xl font-semibold m-0">Choose Training</h1>
            {#if $recommendedTraining.status === DataStatus.RESYNCING}
              <i class='fa-solid fa-lg fa-spinner-third fa-spin' title="Resyncing..."></i>
            {/if}
          </div>
          <div>
            <h2 class="mb-2 text-lg font-semibold">Generate Workout</h2>
            <div class="flex flex-col gap-1 rounded-lg border border-border bg-white p-4 shadow-sm hover:shadow">
              <WorkoutGenerator bind:xssTargetsResponse bind:magicBucketsFieldsAndData date={currentDay} />
            </div>
          </div>
          {#if $recommendedTraining.status !== DataStatus.READY && $recommendedTraining.status !== DataStatus.RESYNCING}
            <div>
              <h2 class="mb-2 text-lg font-semibold">Recommended</h2>
              <HorizontalScrollingTrainingCardsSkeleton/>
            </div>
            <div>
              <h2 class="mb-2 text-lg font-semibold">Recent</h2>
              <HorizontalScrollingTrainingCardsSkeleton/>
            </div>
            <div>
              <h2 class="mb-2 text-lg font-semibold">Favourites</h2>
              <HorizontalScrollingTrainingCardsSkeleton/>
            </div>
          {:else}
            <div>
              <div class='flex flex-row items-center gap-4 mb-2 mt-5'>
                <h2 class="m-0 text-lg font-semibold">{recommendedTrainingExerciseGroups['Recommended'].title}</h2>
                <Button variant='outline' size='sm' class='space-x-1' on:click={() => { filterDialogOpen = true; }}>
                  <span>Filters</span>
                  <i class='fa-light fa-sliders-up relative'>
                    {#if recommendedTrainingSearchFilters.isFilterApplied()}
                      <div class='absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500' />
                    {/if}
                  </i>
                </Button>
              </div>
              {#if recommendedTrainingExerciseGroups['Recommended'].exercises.length == 0}
                <p class="text-sm text-gray-600">No recommended training available at this time.</p>
              {:else if filteredRecommendedTrainingExercises.length == 0}
                <p class="text-sm text-gray-600">No recommended training matches current filters.</p>
              {:else}
                {#if xssTargetsResponse && recommendedTrainingSearchFilters.sortBy.value == 'recommended'}
                  <div class='mb-2'>
                    Recommended Training is ordered to best match your current XSS targets from above: <XSSDisplay xss={xssTargetsResponse.remaining} />
                  </div>
                {/if}
                <HorizontalScrollingTrainingCards exercises={filteredRecommendedTrainingExercises} />
              {/if}
            </div>
            <Dialog.Root bind:open={filterDialogOpen} portal="#shadcn-ui-dialog-target">
              <Dialog.Content class="flex flex-col max-h-[95dvh] md:h-[600px] max-w-[420px] w-[95vw] overflow-hidden rounded-xl px-3">
                <Dialog.Header class='px-3'>
                  <Dialog.Title>
                    Filters
                  </Dialog.Title>
                </Dialog.Header>
                <div class='overflow-y-auto px-3'>
                  <SearchFilter bind:fields={recommendedTrainingSearchFilters} includeTrainingTypeFilters hideResetButton />
                </div>
                <Dialog.Footer class='gap-2 flex-row justify-end'>
                  <Button variant='outline' on:click={() => {
                    if (!recommendedTrainingSearchFilters) return;
                    recommendedTrainingSearchFilters.resetFilters();
                    // Force an assignment so Svelte propagates changes
                    recommendedTrainingSearchFilters = recommendedTrainingSearchFilters;
                  }}>
                    Reset
                  </Button>
                  <Button variant='outline' on:click={() => filterDialogOpen = false}>
                    Apply
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Root>
            <div>
              <h2 class="mb-2 text-lg font-semibold">{recommendedTrainingExerciseGroups['Recent'].title}</h2>
              {#if recommendedTrainingExerciseGroups['Recent'].exercises.length == 0}
                <p class="text-sm text-gray-600">Recent training will appear here.</p>
              {:else}
                <HorizontalScrollingTrainingCards exercises={recommendedTrainingExerciseGroups['Recent'].exercises} />
              {/if}
            </div>
            <div>
              <h2 class="mb-2 text-lg font-semibold">{recommendedTrainingExerciseGroups['Favourites'].title}</h2>
              {#if recommendedTrainingExerciseGroups['Favourites'].exercises.length == 0}
                <p class="text-sm text-gray-600">Favourited or liked training will appear here.</p>
              {:else}
                <HorizontalScrollingTrainingCards exercises={recommendedTrainingExerciseGroups['Favourites'].exercises} />
              {/if}
            </div>
          {/if}
        </div>
      {/if}
    </div>

    <StickyScrollColumn className="hidden w-[300px] lg:flex flex-col gap-8 pt-4">
      <UserInfo {athlete} {workoutDescription} {lastBreakthrough} />
      {#if !$trainingPlan?.isForecastPlan}
        <!-- TODO (hyde): resync and skeleton loading animation -->
        <XataColumn {myFitness} />
      {/if}
      {#if $trainingPlan?.isForecastPlan}
        {#await forecastPromise}
          <aside class="w-full shrink-0 space-y-4">
            <div class="px-2 text-xs uppercase tracking-wide text-gray-500">Next 3 days</div>
            <Skeleton class='w-full h-[100px]'/>
            <Skeleton class='w-full h-[100px]'/>
            <Skeleton class='w-full h-[100px]'/>
          </aside>
        {:then}
          <UpcomingTraining isLoading={isResyncingForecast} {myFitness} forecast_activities={forecastResult?.forecast_activities} activities_3day={forecastResult?.activities} spFocus_3day={forecastResult?.spFocus} lastBTDate={forecastResult?.last_BT_date} {isForecastUpToDate} {lastForecastInputs} />
        {:catch error}
          <div>Error loading forecast: {error}</div>
        {/await}
      {/if}
    </StickyScrollColumn>
  </div>
  <CalendarAddModal {xssTargetsResponse} bind:this={CalendarAddModalElement} root={rootDiv} {sport_type} {activity_types} {myFitness} {likeScore} {dislikeScore} {program_weeks} {challenge_days} weather={$locationPermission && $weather ? $weather : null} />
</div>
